import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency, calculateNetWorth } from '../gameData';
import { getTopScores, submitScore, qualifies, LEADERBOARD_SIZE } from '../leaderboard';

function GameOverDialog({ show, onPlayAgain, onClose, player, reason }) {
  const netWorth = calculateNetWorth(player);
  const bankrupt = reason === 'bankrupt';

  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qualified, setQualified] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [initials, setInitials] = useState('');
  const [newEntryTs, setNewEntryTs] = useState(null);

  // Load the board and decide whether this run earns a spot, each time the
  // game-over dialog opens.
  useEffect(() => {
    if (!show) return;
    let active = true;
    setLoading(true);
    setSubmitted(false);
    setInitials('');
    setNewEntryTs(null);

    getTopScores().then(top => {
      if (!active) return;
      setScores(top);
      // Only winners (in the black) make the board — bankrupt or underwater
      // runs don't qualify.
      setQualified(netWorth > 0 && qualifies(netWorth, top));
      setLoading(false);
    });

    return () => { active = false; };
  }, [show, netWorth]);

  const handleSubmit = useCallback(async () => {
    const cleaned = initials.trim().toUpperCase().slice(0, 3);
    if (!cleaned) return;

    const entry = {
      initials: cleaned,
      netWorth: Math.round(netWorth),
      day: player.maxDays,
      date: new Date().toISOString().slice(0, 10),
      ts: Date.now(),
    };
    const updated = await submitScore(entry);
    setScores(updated);
    setNewEntryTs(entry.ts);
    setSubmitted(true);
  }, [initials, netWorth, player.maxDays]);

  if (!show) return null;

  const showInitialsEntry = qualified && !submitted && !loading;

  return (
    <dialog open={show} className="gameover-dialog">
      <h2>{bankrupt ? '🌊 Sleeping With the Fishes' : netWorth > 0 ? '🎉 Game Over' : '💀 Game Over'}</h2>
      {bankrupt && (
        <p className="bankrupt-message">
          Broke, maxed out, and out of moves. The loan sharks ran out of patience
          and built you a new home — at the bottom of the ocean. 🐟⚰️
        </p>
      )}
      <p className="final-score">
        Final Net Worth: <strong>{formatCurrency(netWorth)}</strong>
      </p>

      {loading && <p>Loading leaderboard…</p>}

      {showInitialsEntry && (
        <div className="highscore-entry">
          <p className="highscore-banner">🏆 New High Score! You made the Top {LEADERBOARD_SIZE}!</p>
          <label htmlFor="initials">Enter your initials:</label>
          <input
            id="initials"
            type="text"
            value={initials}
            maxLength={3}
            autoFocus
            placeholder="AAA"
            onChange={(e) => setInitials(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
          />
          <button className="primary-button" onClick={handleSubmit} disabled={!initials.trim()}>
            Save Score
          </button>
        </div>
      )}

      {!loading && !showInitialsEntry && (
        <>
          {!qualified && (
            <p className="no-highscore">You didn't crack the Top {LEADERBOARD_SIZE} this time.</p>
          )}
          <h3>🏅 Top {LEADERBOARD_SIZE} Kingpins</h3>
          {scores.length === 0 ? (
            <p>No scores yet — you could be the first!</p>
          ) : (
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Initials</th>
                  <th>Net Worth</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {scores.map((s, i) => (
                  <tr key={s.ts ?? i} className={s.ts && s.ts === newEntryTs ? 'new-entry' : ''}>
                    <td>{i + 1}</td>
                    <td>{s.initials}</td>
                    <td>{formatCurrency(s.netWorth)}</td>
                    <td>{s.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <div className="dialog-buttons">
        <button className="primary-button" onClick={onPlayAgain}>Play Again</button>
        <button onClick={onClose}>Close</button>
      </div>
    </dialog>
  );
}

export default GameOverDialog;
